import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from '../../api';

// Severity palette — matches the iASM FindingsTab badges.
const SEV_STYLE = {
  CRITICAL: { bg: '#dc2626', color: '#fff' },
  HIGH:     { bg: '#ea580c', color: '#fff' },
  MEDIUM:   { bg: '#d97706', color: '#fff' },
  LOW:      { bg: '#16a34a', color: '#fff' },
  INFO:     { bg: '#6b7280', color: '#fff' },
};
const SEV_ORDER = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'INFO'];
// Framework chip colours for the compliance matrix (control prefixes from complianceControls).
const FW_COLOR = { SOC2: '#7c3aed', ISO27001: '#0891b2', NIST: '#16a34a', PCI: '#2563eb', GDPR: '#ea580c' };

const badge = (sev) => ({
  ...(SEV_STYLE[sev] || SEV_STYLE.INFO),
  padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 700, whiteSpace: 'nowrap',
});

/**
 * CVE Playbooks tab — productized detection & remediation playbooks (ported from the public
 * Cataam CVE-Lab). Lists findings the playbooks produced for the org and lets the user run them.
 * Each finding shows its remediation steps and the SOC 2 / ISO 27001 / NIST / PCI control mapping.
 */
export default function CvePlaybookTab({ orgId }) {
  const { t } = useTranslation();
  const [findings, setFindings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [runMsg, setRunMsg] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const fetchFindings = useCallback(() => {
    setLoading(true);
    const params = orgId ? { orgId } : {};
    axios.get('/iasm/cve-playbooks/findings', { params })
      .then((r) => setFindings(Array.isArray(r.data) ? r.data : []))
      .catch(() => setFindings([]))
      .finally(() => setLoading(false));
  }, [orgId]);

  useEffect(() => { fetchFindings(); }, [fetchFindings]);

  const runPlaybooks = () => {
    setRunning(true);
    setRunMsg(null);
    axios.post('/iasm/cve-playbooks/run', {})
      .then((r) => { setRunMsg(r.data?.message || t('CvePlaybookTab.scanComplete')); fetchFindings(); })
      .catch(() => setRunMsg(t('CvePlaybookTab.scanFailed')))
      .finally(() => setRunning(false));
  };

  const counts = findings.reduce((acc, f) => { acc[f.severity] = (acc[f.severity] || 0) + 1; return acc; }, {});

  return (
    <div>
      {/* Header + run */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <div>
          <h3 style={{ margin: 0, fontSize: '18px', color: '#111827' }}>
            <i className="fas fa-bullseye" style={{ marginRight: '8px', color: '#dc2626' }} />{t('CvePlaybookTab.title')}
          </h3>
          <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '2px' }}>
            {t('CvePlaybookTab.subtitle')}
          </div>
        </div>
        <button
          onClick={runPlaybooks}
          disabled={running}
          style={{
            marginLeft: 'auto', padding: '8px 16px', borderRadius: '6px', border: 'none',
            background: running ? '#9ca3af' : '#dc2626', color: '#fff', fontWeight: 600,
            fontSize: '13px', cursor: running ? 'default' : 'pointer',
          }}
        >
          <i className={`fas ${running ? 'fa-spinner fa-spin' : 'fa-play'}`} style={{ marginRight: '6px' }} />
          {running ? t('CvePlaybookTab.scanning') : t('CvePlaybookTab.runButton')}
        </button>
      </div>

      {runMsg && (
        <div style={{ marginBottom: '14px', padding: '8px 12px', borderRadius: '6px', background: '#f0fdf4', color: '#166534', fontSize: '13px' }}>
          {runMsg}
        </div>
      )}

      {/* KPI strip */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
        {SEV_ORDER.filter((s) => counts[s]).map((s) => (
          <div key={s} style={{ padding: '8px 14px', borderRadius: '8px', border: '1px solid #e5e7eb', background: '#fff', minWidth: '90px' }}>
            <div style={{ fontSize: '20px', fontWeight: 700, color: SEV_STYLE[s].bg }}>{counts[s]}</div>
            <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: 600 }}>{s}</div>
          </div>
        ))}
        <div style={{ padding: '8px 14px', borderRadius: '8px', border: '1px solid #e5e7eb', background: '#fff', minWidth: '90px' }}>
          <div style={{ fontSize: '20px', fontWeight: 700, color: '#111827' }}>{findings.length}</div>
          <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: 600 }}>{t('CvePlaybookTab.total')}</div>
        </div>
      </div>

      {/* Findings list */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
          <i className="fas fa-spinner fa-spin" style={{ marginRight: '8px' }} />{t('CvePlaybookTab.loading')}
        </div>
      ) : findings.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px 20px', color: '#6b7280', border: '1px dashed #e5e7eb', borderRadius: '8px' }}>
          <i className="fas fa-shield-alt" style={{ fontSize: '28px', color: '#16a34a', marginBottom: '10px' }} />
          <div style={{ fontWeight: 600, color: '#111827' }}>{t('CvePlaybookTab.emptyTitle')}</div>
          <div style={{ fontSize: '13px' }}>{t('CvePlaybookTab.emptyDescription')}</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {findings.map((f) => (
            <div key={f.id} style={{
              border: expanded === f.id ? '1px solid #3b82f6' : '1px solid #e5e7eb',
              borderRadius: '8px', overflow: 'hidden', background: '#fff',
            }}>
              <div onClick={() => setExpanded(expanded === f.id ? null : f.id)}
                style={{ padding: '14px 16px', cursor: 'pointer', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <span style={{ ...badge(f.severity), marginTop: '2px' }}>{f.severity}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: '14px', color: '#111827' }}>{f.title}</div>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    <span>{f.findingType}</span>
                    {f.evidence?.target && <span>· <strong>{f.evidence.target}</strong></span>}
                    {f.mitreTechniqueId && (
                      <span style={{ color: '#7c3aed' }}>· <i className="fas fa-shield-virus" style={{ fontSize: '10px' }} /> {f.mitreTechniqueId}</span>
                    )}
                  </div>
                </div>
                <i className={`fas fa-chevron-${expanded === f.id ? 'up' : 'down'}`} style={{ color: '#9ca3af', marginTop: '4px' }} />
              </div>

              {expanded === f.id && (
                <div style={{ padding: '0 16px 16px 16px', borderTop: '1px solid #f3f4f6' }}>
                  {f.description && (
                    <p style={{ fontSize: '13px', color: '#374151', marginTop: '12px' }}>{f.description}</p>
                  )}

                  {f.remediationSteps && (
                    <div style={{ marginTop: '10px' }}>
                      <div style={{ fontWeight: 600, fontSize: '12px', color: '#111827', marginBottom: '4px' }}>
                        <i className="fas fa-wrench" style={{ marginRight: '6px', color: '#2563eb' }} />{t('CvePlaybookTab.remediation')}
                      </div>
                      <pre style={{
                        whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: '13px', color: '#374151',
                        background: '#f9fafb', borderRadius: '6px', padding: '10px 12px', margin: 0,
                      }}>{f.remediationSteps}</pre>
                    </div>
                  )}

                  {Array.isArray(f.complianceControls) && f.complianceControls.length > 0 && (
                    <div style={{ marginTop: '12px' }}>
                      <div style={{ fontWeight: 600, fontSize: '12px', color: '#111827', marginBottom: '6px' }}>
                        <i className="fas fa-shield-alt" style={{ marginRight: '6px', color: '#7c3aed' }} />{t('CvePlaybookTab.complianceImpact')}
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {f.complianceControls.map((c) => {
                          const fw = String(c).split(':')[0];
                          return (
                            <span key={c} style={{
                              padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600,
                              background: (FW_COLOR[fw] || '#6b7280') + '1a',
                              color: FW_COLOR[fw] || '#374151',
                              border: `1px solid ${(FW_COLOR[fw] || '#6b7280')}40`,
                            }}>{c}</span>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {f.evidence && (
                    <div style={{ marginTop: '12px' }}>
                      <div style={{ fontWeight: 600, fontSize: '12px', color: '#111827', marginBottom: '4px' }}>
                        <i className="fas fa-fingerprint" style={{ marginRight: '6px', color: '#6b7280' }} />{t('CvePlaybookTab.evidence')}
                      </div>
                      <pre style={{
                        fontSize: '12px', color: '#374151', background: '#f9fafb', borderRadius: '6px',
                        padding: '10px 12px', margin: 0, overflowX: 'auto',
                      }}>{JSON.stringify(f.evidence, null, 2)}</pre>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
